import React, { useState, useCallback, useEffect } from 'react'
import { Col, Form, Row, Alert, Spinner, Table } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import ArraySelect from '../../Select/ArraySelect'
import { colors } from '../../../styles/colors'
import { OverlaySpinnerWrapper } from '../../Forms/StyledInputLoadingOverlay'
import useMountEffect from '../../../utils/hooks/useMountEffect'
import { emptyGuid, isEmptyGuid } from '../../../utils/emptyGuid'
import { useParams } from 'react-router-dom'
import { getLanguageValue } from '../../../utils/getLanguageValue';
import apiClient from '../../../utils/api/apiClient'
import { APIEndpoints } from '../../../utils/api/apiConfig'
import { makeCall, setStateByKey } from '../../../utils/api/makeCall'
import { ButtonWithLoadingState } from '../../../components/Buttons'
import { getMetaProperty } from '../../../utils/customerPrototypes'

const StyledHeaderTr = styled.tr`
  th {
    .pending-delivery-info {
      font-size: small;
      color: gray;
      font-weight: initial;
    }
  }
`
const ResponsiveTableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;

  @media only screen and (max-width: 767px),
    (min-device-width: 320px) and (max-device-width: 767px) {
    .react-bs-table-no-data {
      display: none;
    }

    table {
      empty-cells: show;
      width: 100%;
    }

    thead > tr > th {
      width: 50%;
      display: inline-block;
      border: 0.5px solid gray;
      text-align: center;
    }

    .fullWidthMobile {
      width: 100%;
      display: inline-block;
    }

    tbody,
    td,
    tr {
      display: block;
    }

    th::after {
      display: none;
    }

    tr:nth-child(2n) {
      background: #ccc;
    }

    td {
      border: none;
      border-bottom: 1px solid #eee;
      empty-cells: show !important;
      position: relative;
      white-space: normal;
      text-align: left;
    }

    td::before {
      position: absolute;
      top: 6px;
      left: 6px;
      width: 44%;
      white-space: nowrap;
      text-align: left;
      font-weight: bold;
    }

    .actions-header-cell,
    .checkBox-header-cell {
      display: none;
    }

    .actions-cell {
      width: 100% !important;
    }
  }
`

const StyledButtonsWrapper = styled.div`
  @media screen and (max-width: 767px) {
    display: flex;
    flex-direction: column;
    width: 100%;
    .btn {
      margin: 3px 0;
    }
  }
`

const StyledLine = styled.div`
  opacity: 0.8;
  background:${colors.gray1};
  padding: 0 45px 0 20px;
  `

const AlertMessagesWrapper = styled.div`
  max-width: 600px;
`

const DispositionThreshold = () => {
    const { t } = useTranslation()
    const { articleId } = useParams()
    const [sizeSystems, setSizeSystems] = useState([])
    const [procurementGroups, setProcurementGroups] = useState([])
    const [dispoData, setDispoData] = useState(null)
    const [selectedProcurementGroupId, setSelectedProcurementGroupId] = useState(null)
    const [successStates, setSuccessStates] = useState({})
    const [errorStates, setErrorStates] = useState({})
    const [loadingStates, setLoadingStates] = useState({})
    const loadProcurementGroups = useCallback(async () => {
        makeCall('loadProcurementGroups', async () => {
            let loadedProcurementGroups = await apiClient.getJson(APIEndpoints.procurementGroups)
            setProcurementGroups(loadedProcurementGroups)
            if ((loadedProcurementGroups?.length ?? 0) === 1) {
                setSelectedProcurementGroupId(loadedProcurementGroups[0].id)
            }
        }, setErrorStates, setLoadingStates)

    }, [setSelectedProcurementGroupId])
    const loadDispoSettings = useCallback(async () => {
        makeCall('loadDispoSettings', async () => {
            let loadedDispoSettings = await apiClient.getJson(APIEndpoints.articleAdminDispoSettings(articleId, selectedProcurementGroupId))
            setDispoData(loadedDispoSettings)
        }, setErrorStates, setLoadingStates)

    }, [articleId, selectedProcurementGroupId])
    const saveDispoSettings = () => {
        makeCall('saveDispoSettings', async () => {
            await apiClient.postJson(APIEndpoints.articleAdminDispoSettings(articleId, selectedProcurementGroupId), dispoData)
            await loadDispoSettings()
            setStateByKey(setSuccessStates, 'saveDispoSettings', true)
        }, setErrorStates, setLoadingStates)
    }
    const loadSizeSystems = useCallback(() => {
        makeCall('loadSizeSystems', async () => {
            let loadedSizeSystems = await apiClient.getJson(APIEndpoints.articleAdmin(articleId).sizeSystems)
            setSizeSystems(loadedSizeSystems)
        }, setErrorStates, setLoadingStates)
    }, [articleId])

    useMountEffect(() => {
        loadSizeSystems()
        loadProcurementGroups()
    })

    useEffect(() => {
        if (!isEmptyGuid(selectedProcurementGroupId)) {
            loadDispoSettings()
        }
    }, [selectedProcurementGroupId, loadDispoSettings])

    const updateVariantValue = (e) => {
        const { target } = e
        let key = target.name
        let value = null
        const [/*fieldName*/, sizeId] = target.id.split('_')
        switch (key) {
            case 'supplierDeliveryTime':
                value = parseInt(target.value)
                setVariantValue(sizeId, key, value)
                break
            case 'reorderLevel':
                value = parseFloat(target.value)
                setVariantValue(sizeId, key, value)
                break
            default:
                break
        }
    }
    const setVariantValue = (sizeId, key, value) => {
        if (!dispoData?.variants)
            return

        let newVariants = [...dispoData.variants]
        let foundIndex = newVariants.findIndex(item => item.sizeId === sizeId)
        if (foundIndex >= 0) {
            newVariants[foundIndex][key] = value
        }
        else {
            let newItem = {
                sizeId: sizeId,
                reorderLevel: null,
                supplierDeliveryTime: null,
            }
            newItem[key] = value
            newVariants.push(newItem)
        }
        setDispoData((prev) => ({
            ...prev,
            variants: newVariants,
        }))
    }
    const getVariantValue = (sizeId) => {
        if (!dispoData?.variants)
            return null
        let variantData = dispoData.variants.find(item => item.sizeId === sizeId)
        return variantData
    }
    const isCalculatedByServiceIsHidden = getMetaProperty(dispoData?.metaProperties, `/isCalculatedByService`, 'isHidden')

    const renderVariantIndependentData = () => {
        const variantData = getVariantValue(emptyGuid);
        const supplierDeliveryTime = variantData?.supplierDeliveryTime ?? -1;
        const supplierDeliveryTimeIsHidden = getMetaProperty(variantData?.metaProperties, `/supplierDeliveryTime`, 'isHidden')
        const supplierDeliveryTimeIsDisabled = getMetaProperty(variantData?.metaProperties, `/supplierDeliveryTime`, 'isReadOnly')
        const reorderLevel_IsHidden = getMetaProperty(variantData?.metaProperties, `/reorderLevel`, 'isHidden')
        return (
            <>
                {!reorderLevel_IsHidden && <Row className='mb-3'>
                    <Form.Group as={Row}>
                        <Form.Label column>
                            {t('articleAdmin:reorderLevel')}
                        </Form.Label>
                        <Col>
                            <OverlaySpinnerWrapper>
                                <Form.Control
                                    id={`reorderLevel_${emptyGuid}`}
                                    value={variantData?.reorderLevel}
                                    name='reorderLevel'
                                    type='number'
                                    min='0'
                                    onWheel={(e) => e.target.blur()}
                                    onChange={updateVariantValue}
                                    disabled={dispoData?.isCalculatedByService}
                                />
                            </OverlaySpinnerWrapper>
                        </Col>
                    </Form.Group>
                </Row>}
                {!supplierDeliveryTimeIsHidden && <Row className='mb-3'>
                    <Form.Group as={Row}>
                        <Form.Label column>
                            {t('articleAdmin:deliveryTime')}
                        </Form.Label>
                        <Col>
                            <OverlaySpinnerWrapper>
                                <Form.Control
                                    id={`supplierDeliveryTime_${emptyGuid}`}
                                    value={supplierDeliveryTime >= 0 ? supplierDeliveryTime : ''}
                                    name='supplierDeliveryTime'
                                    type='number'
                                    min='0'
                                    onWheel={(e) => e.target.blur()}
                                    onChange={updateVariantValue}
                                    disabled={supplierDeliveryTimeIsDisabled}
                                />
                            </OverlaySpinnerWrapper>
                        </Col>
                    </Form.Group>
                </Row>}
            </>
        )
    }
    const renderVariantDataAsTable = (size) => {
        const sizeId = size.id
        const variantData = getVariantValue(sizeId);
        const supplierDeliveryTime = variantData?.supplierDeliveryTime ?? -1;
        const supplierDeliveryTimeIsHidden = !dispoData?.showVariantsDeliveryTime || getMetaProperty(variantData?.metaProperties, `/supplierDeliveryTime`, 'isHidden')
        return (
            <>
                <tr>
                    <td>
                        {getLanguageValue(size.name.values)}
                    </td>
                    <td>
                        <OverlaySpinnerWrapper>
                            <Form.Control
                                id={`reorderLevel_${sizeId}`}
                                value={variantData?.reorderLevel}
                                name='reorderLevel'
                                type='number'
                                min='0'
                                onWheel={(e) => e.target.blur()}
                                onChange={updateVariantValue}
                                disabled={dispoData?.isCalculatedByService}
                            />
                        </OverlaySpinnerWrapper>
                    </td>
                    {!supplierDeliveryTimeIsHidden && <td>
                        <OverlaySpinnerWrapper>
                            <Form.Control
                                id={`supplierDeliveryTime_${sizeId}`}
                                value={supplierDeliveryTime >= 0 ? supplierDeliveryTime : ''}
                                name='supplierDeliveryTime'
                                type='number'
                                min='0'
                                onWheel={(e) => e.target.blur()}
                                onChange={updateVariantValue}
                            />
                        </OverlaySpinnerWrapper>

                    </td>}
                </tr>
            </>
        )
    }
    const renderBaseData = () => {
        return (
            <>
                <Row className='mb-3'>
                    <Form.Group as={Row}>
                        <Form.Label column>
                            {t('articleAdmin:supplier')}
                        </Form.Label>
                        <Col>
                            <OverlaySpinnerWrapper>
                                <Form.Control
                                    id='supplierdisplayName'
                                    name='supplierdisplayName'
                                    /*placeholder={t('articleAdmin:supplier')}*/
                                    value={dispoData?.supplier?.displayName ?? ''}
                                    disabled={true}
                                />
                            </OverlaySpinnerWrapper>
                        </Col>
                    </Form.Group>
                </Row>
                {!isCalculatedByServiceIsHidden && <Row className='mb-3'>
                    <Form.Group as={Row}>
                        <Form.Label column></Form.Label>
                        <Col>
                            <OverlaySpinnerWrapper>
                                <Form.Check
                                    type='checkbox'
                                    id={`isCalculatedByService`}
                                    name='isCalculatedByService'
                                    label={t('articleAdmin:automaticallyCalculateReorderLevel')}
                                    checked={dispoData?.isCalculatedByService}
                                    onChange={(e) => setDispoData((prev) => ({
                                        ...prev,
                                        isCalculatedByService: e.target.checked,
                                    }))}
                                />
                            </OverlaySpinnerWrapper>
                        </Col>
                    </Form.Group>
                </Row>}
                {renderSubTitle(t('sizeIndependent'))}
                {renderVariantIndependentData()}
            </>
        )
    }
    const renderSubTitle = (title) => {
        return (
            <>
                <div className='mb-3'>
                    <StyledLine>
                        <h2>{title.toUpperCase()}</h2>
                    </StyledLine>
                </div>
            </>
        )
    }
    const hasSizes = () => {
        if (!sizeSystems)
            return false

        let sizesExists = sizeSystems.some(item => !!item.sizes?.length)
        return !!sizesExists
    }
    const renderSizeSystemData = () => {
        if (loadingStates.loadSizeSystems) {
            return <div className="d-flex align-items-center justify-content-center"><Spinner animation='border' variant='primary' /></div>
        }
        return (
            <>
                {hasSizes() &&
                    renderSubTitle(t('sizeDependent'))
                }
                {sizeSystems.map((sizeSystem) => (
                    (sizeSystem.sizes?.length??0) ? (<React.Fragment key={sizeSystem.id}>
                        {sizeSystems.length > 1 &&
                            <h2 className='h1'>
                                {getLanguageValue(sizeSystem.internalName.values)}
                            </h2>}
                        {renderSizeData(sizeSystem.sizes)}
                    </React.Fragment>) : null
                ))}
            </>
        )
    }
    const showDeliveryTime = () => {
        if(!dispoData?.showVariantsDeliveryTime)
            return false
        if (!dispoData?.variants)
            return false

        let deliveryTimes = dispoData.variants.filter(item => !getMetaProperty(item.metaProperties, `/supplierDeliveryTime`, 'isHidden'))
        return !!deliveryTimes?.length
    }
    const renderSizeData = (sizes) => {
        return (
            <>
                <ResponsiveTableWrapper>
                    <Table borderless>
                        <tbody>
                            <StyledHeaderTr>
                                <th>{t('size')}</th>
                                <th>{t('articleAdmin:reorderLevel')}</th>
                                {showDeliveryTime() && <th>{t('articleAdmin:deliveryTime')}</th>}
                            </StyledHeaderTr>
                            {sizes.map((size) => (
                                <React.Fragment key={size.id}>
                                    {renderVariantDataAsTable(size)}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </Table>
                </ResponsiveTableWrapper>
            </>
        )
    }
    const renderProcurementGroupSelection = () => {
        if (loadingStates.loadProcurementGroups) {
            return (
                <div className='d-flex align-items-center justify-content-center'>
                    <Spinner animation='border' variant='primary' />
                </div>
            )
        }
        if ((procurementGroups?.length ?? 0) === 1) {
            return null
        }
        return (
            <>
                <Row className='mb-3'>
                    <Form.Group as={Row}>
                        <Form.Label column>
                            {t('reportConfig:groupSelection')}
                        </Form.Label>
                        <Col>
                            <OverlaySpinnerWrapper>
                                <ArraySelect
                                    name='procurementGroups'
                                    value={selectedProcurementGroupId}
                                    defaultOption={{ value: emptyGuid, name: t('pleaseSelect') }}
                                    defaultOptionIsSelectable
                                    options={procurementGroups}
                                    noAutoSelect
                                    onChange={e => setSelectedProcurementGroupId(e.target.value)}
                                    returnEvent
                                />
                            </OverlaySpinnerWrapper>
                        </Col>
                    </Form.Group>
                </Row>
            </>
        )
    }

    const renderDispositionThreshold = () => {
        if (loadingStates.loadDispoSettings) {
            return (
                <div className='d-flex align-items-center justify-content-center'>
                    <Spinner animation='border' variant='primary' />
                </div>
            )
        }
        return (
            <>

                {renderBaseData()}
                {renderSizeSystemData()}
                <StyledButtonsWrapper className='d-flex justify-content-start'>
                    <ButtonWithLoadingState
                        className='m-2'
                        buttonText={t('saveButton')}
                        isLoading={loadingStates.saveDispoSettings}
                        onClick={saveDispoSettings}
                        disabled={false}
                    />
                </StyledButtonsWrapper>
            </>
        )
    }
    return (
        <div>
            {renderProcurementGroupSelection()}
            {!isEmptyGuid(selectedProcurementGroupId) && renderDispositionThreshold()}
            <AlertMessagesWrapper>
                {errorStates.saveDispoSettings && <Alert variant='danger'>
                    {t('articleAdminOverlayProgress:danger')/*errorStates.saveDispoSettings.value*/}
                </Alert>}
                {successStates.saveDispoSettings && <Alert variant='success' className='mt-2'>
                    {t('articleAdmin:successMsg') /*successStates.saveDispoSettings.value*/}
                </Alert>}
            </AlertMessagesWrapper>
        </div>
    )
}

export default DispositionThreshold
